import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { apiCreateListingApplication } from '#/api/listingApplication/apiCreateListingApplication';
import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { Text } from '#/components/Text/Text';
import { useTemporaryStorageContext } from '#/containers/TemporaryStorageContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { userDashboardApiDeleteListingApplication } from '#/modules/userDashboard/api/listingApplication/userDashboardApiDeleteListingApplication';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';
import { trackDisplayToast } from '#/utils/analytics/track/trackToast';
import { LISTING_TYPE_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypeHumanNameMap';
import { showModal } from '#/zustand-stores/modalStore';
import { dismissToast, showToast } from '#/zustand-stores/toastsStore';

type Args = {
  job: ApiJob;
  initialListingApplicationId: ApiListingApplicationId | null;
};

export function useJobApplicationStatus({
  job,
  initialListingApplicationId,
}: Args) {
  const navigate = useNavigate();

  const { user } = useUser();
  const { i18n } = useI18n();

  const [listingApplicationId, setListingApplicationId] = useKeyedState(
    initialListingApplicationId,
    { key: job.id },
  );

  const { temporaryStorage, saveTemporaryStorage } =
    useTemporaryStorageContext();

  const showMarkAppliedToast = useCallback(() => {
    trackDisplayToast({
      type: 'success_mark_applied',
      current_page_url: window.location.href,
    });

    showToast({
      actions: [
        {
          label: i18n('See All'),
          onClick: () => {
            trackClicked('Action Toast', {
              type: 'success_mark_applied',
              current_page_url: window.location.href,
            });
            dismissToast('success-mark-applied');
            void navigate('/dashboard/application-history');
          },
          type: 'button',
        },
      ],
      headline: i18n("You've marked this listing as applied"),
      onDismiss: () => {
        trackEventOnce('Dismissed: Action Toast', {
          type: 'success_mark_applied',
          current_page_url: window.location.href,
        });
      },

      dismissible: true,
      durationMs: 15_000,
      id: 'success-mark-applied',
      variant: 'info',
    });
  }, [navigate, i18n]);

  const markAsAppliedAsGuest = useCallback(async () => {
    trackEvent('Clicked: Marked Applied (Logged Out)', {
      listing_id: job.id,
      listing_type: 'JOB',
      org_id: job.org?.id ?? null,
      org_type: job.org?.orgType ?? null,
    });

    const tmpId = await saveTemporaryStorage([
      {
        type: 'LISTING_APPLICATION_VALUES',
        data: {
          id: job.id,
          type: 'JOB',
          requestedAttachmentsHash: '',
        },
      },
    ]);

    const redirectTo = `${window.location.pathname}${
      window.location.search
    }${window.location.search ? '&' : '?'}tmpId=${tmpId || ''}`;

    showModal('SIGNUP', {
      title: i18n('Just One More Step!'),
      description: (
        <Text mb={4}>
          {i18n(
            'Create an account on Idealist in order to mark this %(listingType)s as applied.',
            { listingType: LISTING_TYPE_HUMAN_NAME_MAP(i18n).JOB },
          )}
        </Text>
      ),
      redirectTo,
    });
  }, [job.id, job.org?.id, job.org?.orgType, saveTemporaryStorage, i18n]);

  const markAsApplied = useCallback(async () => {
    if (!user) {
      return markAsAppliedAsGuest();
    }

    const savedListingApplication = await apiCreateListingApplication({
      id: job.id,
      type: 'JOB',
      requestedAttachmentsHash: '',
    });

    setListingApplicationId(savedListingApplication.id);
    showMarkAppliedToast();
  }, [
    job.id,
    markAsAppliedAsGuest,
    setListingApplicationId,
    showMarkAppliedToast,
    user,
  ]);

  const markAsNotApplied = useCallback(async () => {
    trackEvent('Clicked: Marked Not Applied', {
      listing_id: job.id,
      listing_type: 'JOB',
      org_id: job.org?.id ?? null,
      org_type: job.org?.orgType ?? null,
    });

    await userDashboardApiDeleteListingApplication(listingApplicationId!);
    setListingApplicationId(null);
  }, [
    job.id,
    job.org?.id,
    job.org?.orgType,
    listingApplicationId,
    setListingApplicationId,
  ]);

  const initialStorageValues = temporaryStorage?.initialValues;
  useEffect(() => {
    if (initialStorageValues && job.id === initialStorageValues.id) {
      void markAsApplied();
    }
  }, [initialStorageValues, job.id, markAsApplied]);

  const onApplied = useCallback(
    (listingApplicationId: ApiListingApplicationId) => {
      setListingApplicationId(listingApplicationId);
    },
    [setListingApplicationId],
  );

  return { listingApplicationId, markAsApplied, markAsNotApplied, onApplied };
}
